import React from 'react'
import clsx from 'clsx'
import { useHistory } from 'react-router'
import { useAppSelector, useAppDispatch } from '../../../hooks/redux'
import { useAuth0 } from '@auth0/auth0-react'
import { useTheme } from '@mui/material'

import DownArrowIcon from '../../icons/DownArrowIcon'
import ButtonMenu from '../ButtonMenu'
import MediaAvatar from '../../atoms/MediaAvatar'

import { Routes } from '../../../Routes'
import {
  CUSTOMER_SEARCH_STORAGE_KEY,
  CUSTOM_FIELDS_DROPDOWN_STORAGE_KEY,
  CUSTOM_FIELDS_VALUE_STORAGE_KEY
} from '../../../pages/Customers'
import { USER_SEARCH_STORAGE_KEY } from '@SonarCX/pages/People'
import { TASK_SEARCH_STORAGE_KEY } from '@SonarCX/pages/Tasks/Tabs/AllTasksTab'
import { TASK_CARD_SEARCH_STORAGE_KEY } from '@SonarCX/pages/Tasks/Tabs/CardsTab/Controls'
import { unsubscribePush } from '../../../lib/api'
import { getPushSubscription } from '../../../setupWorker'
import { useEnvironment } from '../../../providers/EnvironmentProvider'
import { selectPresence } from '../../../slices/presence'
import {
  ACCOUNT_KEY,
  getStoredAccount,
  setStoredAccount,
  useAccounts
} from '../../../providers/AccountProvider'
import { AddErrorAlert } from '../../../slices/alert'
import useStyles from './styles'

enum Opts {
  MY_ACCOUNT = 'My Account',
  SIGN_OUT = 'Sign out'
}

interface UserInfoProps {
  setOpen?: (open: boolean) => void
}

const clearStorageVars = () => {
  localStorage.removeItem(CUSTOM_FIELDS_DROPDOWN_STORAGE_KEY)
  localStorage.removeItem(CUSTOMER_SEARCH_STORAGE_KEY)
  localStorage.removeItem(USER_SEARCH_STORAGE_KEY)
  localStorage.removeItem(TASK_SEARCH_STORAGE_KEY)
  localStorage.removeItem(TASK_CARD_SEARCH_STORAGE_KEY)
  localStorage.removeItem(CUSTOM_FIELDS_VALUE_STORAGE_KEY)
}

const UserInfo: React.FC<UserInfoProps> = ({ setOpen = open => {} }) => {
  const dispatch = useAppDispatch()
  const theme = useTheme()
  const history = useHistory()

  const { user } = useEnvironment()
  const { accounts, callEnvironment } = useAccounts()
  const { logout } = useAuth0()
  const { presence } = useAppSelector(selectPresence)

  const [activeStatus, setActiveStatus] = React.useState('online')
  const targetRef = React.useRef<HTMLDivElement>()

  const classes = useStyles({ online: activeStatus })

  const options = React.useMemo(
    () => [Opts.MY_ACCOUNT, ...accounts.map(a => a.name), Opts.SIGN_OUT],
    [accounts]
  )

  const handleLogout = React.useCallback(async () => {
    try {
      const subscription = await getPushSubscription()
      if (subscription) {
        await subscription.unsubscribe()
        await unsubscribePush(subscription)
      }

      localStorage.removeItem('session_user')
      localStorage.removeItem('session_token')
      localStorage.removeItem(ACCOUNT_KEY)
      logout({ logoutParams: { returnTo: `${window.location.origin}/login` } })
    } catch (err) {
      dispatch(AddErrorAlert({ err }))
      console.error('Error signing out', err)
    }
  }, [logout, dispatch])

  const handleSelect = React.useCallback(
    item => {
      if (item === Opts.SIGN_OUT) {
        handleLogout()
        return
      }

      if (item === Opts.MY_ACCOUNT) {
        history.push(`${Routes.USERS}/${user?.id}`)
        return
      }

      if (accounts.map(a => a.name).includes(item)) {
        const id = accounts.find(a => a.name === item).id
        const currentAccount = getStoredAccount()

        if (!currentAccount) {
          setStoredAccount(id)
          clearStorageVars()
          window.location.reload()
        } else {
          if (currentAccount === id) {
            return
          } else {
            localStorage.setItem('session_switch', id)
            clearStorageVars()
            callEnvironment(id)
            setStoredAccount(id)
            localStorage.removeItem('session_switch')
          }
        }
      }
    },
    [accounts, callEnvironment, handleLogout, history, user]
  )

  const renderItem = React.useCallback(
    opt => {
      if (opt === Opts.MY_ACCOUNT) {
        return <div className={classes.dividerBottom}>{opt}</div>
      } else if (opt === Opts.SIGN_OUT) {
        return <div className={classes.dividerTop}>{opt}</div>
      } else if (accounts.map(a => a.name).includes(opt)) {
        const id = accounts.find(a => a.name === opt).id
        const isSelected = getStoredAccount() === id
        const symbol = isSelected ? (
          <span
            style={{
              marginRight: '4px',
              fontSize: theme.size.sm,
              color: theme.palette.primary.main
            }}
          >
            &gt;
          </span>
        ) : null
        return (
          <div>
            {symbol} {opt}
          </div>
        )
      } else {
        return opt
      }
    },
    [accounts, classes.dividerBottom, classes.dividerTop, theme]
  )

  // Look up user presence if not passed down
  React.useEffect(() => {
    // Check if user id is in presence map
    const status = presence[user?.id]
    if (status) {
      setActiveStatus(status)
    }
  }, [presence, user])

  return (
    <div ref={targetRef} className={clsx(classes.root, classes.newVersionRoot)}>
      <MediaAvatar
        user={user}
        maxInitials={1}
        className={clsx(classes.avatar, classes.newVersionAvatar)}
      />

      {/* @ts-ignore */}
      <ButtonMenu
        id='user-menu'
        options={options}
        onSelect={handleSelect}
        className={classes.icon}
        renderFunc={renderItem}
        origin={{
          anchor: { vertical: 'bottom', horizontal: 'right' },
          transform: { vertical: 'top', horizontal: 'right' }
        }}
        targetRef={targetRef}
      >
        <DownArrowIcon onClick={() => setOpen && setOpen(false)} />
      </ButtonMenu>
    </div>
  )
}

export default React.memo(UserInfo)
